import { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button } from 'components/atoms/button'
import { BaseModal } from 'components/organisms/modals/base-modal'

interface DeleteContactsModalProps {
	open: boolean
	setOpen: (open: boolean) => void
	onConfirm: () => void
	loading: boolean
	contactName?: string
	count?: number
	isSelectAll?: boolean
	/** Human-readable description of who is being deleted, e.g. "3 contacts in VIP matching current filters" */
	selectionDescription?: string
	/** 'delete' permanently removes contacts; 'removeFromGroup' only strips group membership. */
	mode?: 'delete' | 'removeFromGroup'
	/** Group name shown in the confirmation copy for 'removeFromGroup'. */
	groupName?: string
}

interface DeleteMessageArgs {
	isRemove: boolean
	contactName?: string
	selectionDescription?: string
	isSelectAll?: boolean
	count: number
	groupName?: string
}

function buildDeleteMessage({
	isRemove,
	contactName,
	selectionDescription,
	isSelectAll,
	count,
	groupName,
}: DeleteMessageArgs): React.ReactNode {
	if (isRemove) {
		const contactWord = count === 1 ? 'contact' : 'contacts'
		const countLabel = `${count.toLocaleString()} ${contactWord}`
		// Preserve truthiness semantics (`||` not `??`): an empty selection
		// description must fall through to the generated count text.
		const targetText =
			contactName ||
			selectionDescription ||
			(isSelectAll ? `all ${count.toLocaleString()} contacts` : countLabel)
		// The bulk selection description already names the group ("3 contacts in
		// VIP") for larger selections, so only append "from <group>" when it's not
		// already there — avoids "remove 3 contacts in VIP from VIP". Match the
		// structured prefix, not an arbitrary substring, so a contact whose name
		// happens to contain the group name isn't mistaken for group context.
		const groupContext = `${countLabel} in ${groupName}`
		const targetNamesGroup = !!groupName && !!selectionDescription?.startsWith(groupContext)
		return (
			<>
				This will remove <span className="text-primary-text font-semibold">{targetText}</span>
				{groupName && !targetNamesGroup ? (
					<>
						{' '}
						from <span className="text-primary-text font-semibold">{groupName}</span>
					</>
				) : null}
				. They will stay in your contact list.
			</>
		)
	}

	if (contactName) {
		return (
			<>
				This will permanently remove <span className="text-primary-text font-semibold">{contactName}</span> from your contact list. This action cannot be undone.
			</>
		)
	}

	if (selectionDescription) {
		return (
			<>
				This will permanently remove <span className="text-primary-text font-semibold">{selectionDescription}</span>. This action cannot be undone.
			</>
		)
	}

	if (isSelectAll) {
		return (
			<>
				This will permanently remove <span className="text-primary-text font-semibold">all {count.toLocaleString()} contacts</span>. This action cannot be undone.
			</>
		)
	}

	return (
		<>
			This will permanently remove <span className="text-primary-text font-semibold">{count.toLocaleString()} {count === 1 ? 'contact' : 'contacts'}</span>. This action cannot be undone.
		</>
	)
}

export function DeleteContactsModal({
	open,
	setOpen,
	onConfirm,
	loading,
	contactName,
	count = 0,
	isSelectAll,
	selectionDescription,
	mode = 'delete',
	groupName,
}: DeleteContactsModalProps) {
	const isSingle = !!contactName || count === 1
	const isRemove = mode === 'removeFromGroup'

	const message = buildDeleteMessage({ isRemove, contactName, selectionDescription, isSelectAll, count, groupName })

	let title = 'Delete Contacts'
	if (isSingle) title = 'Delete Contact'
	if (isRemove) title = 'Remove from group'

	const confirmLabel = isRemove ? 'Remove' : 'Delete'
	const loadingLabel = isRemove ? 'Removing…' : 'Deleting…'

	return (
		<BaseModal
			open={open}
			setOpen={loading ? undefined : setOpen}
			title={title}
			size="sm"
			preventClose={loading}
		>
			<div className="flex flex-col gap-5 text-left">
				<p className="text-sm text-secondary-text leading-relaxed">
					{message}
				</p>

				<div className="border-t border-white/[0.06] pt-4 flex gap-3">
					<Button variant="secondary" fullWidth onClick={() => setOpen(false)} disabled={loading}>
						Cancel
					</Button>
					<Button
						variant="primary"
						fullWidth
						onClick={onConfirm}
						disabled={loading}
					>
						{loading ? (
							<>
								<FontAwesomeIcon icon={['fas', 'spinner']} spin className="mr-2" />
								{loadingLabel}
							</>
						) : (
							confirmLabel
						)}
					</Button>
				</div>
			</div>
		</BaseModal>
	)
}

// ---------------------------------------------------------------------------
// Modal Manager integration
// ---------------------------------------------------------------------------

export interface DeleteContactsModalManagedProps {
	onConfirm: () => void
	loading: boolean
	contactName?: string
	count?: number
	isSelectAll?: boolean
	selectionDescription?: string
	mode?: 'delete' | 'removeFromGroup'
	groupName?: string
}

declare module 'components/organisms/modals/modal-registry' {
	interface ModalRegistry {
		'delete-contacts': DeleteContactsModalManagedProps
	}
}

export function ManagedDeleteContactsModal({
	onClose,
	onConfirm,
	...rest
}: DeleteContactsModalManagedProps & { onClose: () => void }) {
	const [open, setOpen] = useState(true)

	const handleClose = () => {
		setOpen(false)
		setTimeout(onClose, 200)
	}

	return (
		<DeleteContactsModal
			open={open}
			setOpen={(v: boolean) => { if (!v) handleClose() }}
			onConfirm={() => { onConfirm(); handleClose() }}
			{...rest}
		/>
	)
}
